import React from "react";
import { useNavigate } from "react-router";
import { Alert, Button, ListGroup, ListGroupItem } from "reactstrap";

import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { pageDeleteBlockersCleared } from "@/services/pageServices/pagesSlice";

/**
 * SCRUM-1296 — what an editor sees after a page delete is refused.
 *
 * The guard (SCRUM-729 + SCRUM-1259) is right to refuse, but until now the
 * answer was a five-second toast holding one concatenated sentence: the editor
 * was told *no* and given five seconds to memorise which pages to go fix. This
 * is a **work list** instead — it stays until dismissed, gives each referrer its
 * own row, and makes that row open the page so the link can be removed or
 * repointed.
 *
 * Two consequences of that purpose:
 *
 * - The rows are built from `state.pages.page_delete_blockers` — the server's
 *   `referencingPages` array — and never from `errors[0].msg`. Rewording the
 *   server sentence must not change a single row.
 * - The state lives in the store, not here, because walking the list unmounts
 *   this component: opening a referrer is a route change, and coming back must
 *   find the remaining rows still there.
 *
 * Rendered by DisplayPagesView above the table, i.e. outside the Custom
 * Pages / Navigation-Footer toggle, so switching tabs does not drop it either.
 */
const PageDeleteBlockersPanel = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const blockers = useAppSelector((s) => s.pages.page_delete_blockers) || [];

  if (blockers.length === 0) return null;

  return (
    <Alert color="warning" className="text-left" data-testid="page-delete-blockers">
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <strong>This page cannot be deleted yet.</strong>
          <div>
            {blockers.length} other page(s) still link to it. Open each one below, remove
            or repoint the link, then delete again.
          </div>
        </div>
        <Button
          close
          aria-label="Dismiss"
          data-testid="page-delete-blockers-close"
          onClick={() => dispatch(pageDeleteBlockersCleared())}
        />
      </div>

      <ListGroup className="mt-3">
        {blockers.map((page) => (
          <ListGroupItem
            action
            tag="button"
            type="button"
            key={String(page._id)}
            data-testid={`page-delete-blocker-${String(page._id)}`}
            onClick={() =>
              // SCRUM-2354: a staged edit's `_id` is not a page id, so its row
              // opens the staged edits screen rather than the page editor.
              navigate(
                page.kind === "stagedEdit"
                  ? "/admin/updated-pages"
                  : `/admin/update-page-content/${String(page._id)}`
              )
            }
          >
            <strong>{page.global_title || String(page._id)}</strong>
            {page.page_configuration?.path ? (
              <span className="ml-2 text-muted">{page.page_configuration.path}</span>
            ) : null}
          </ListGroupItem>
        ))}
      </ListGroup>
    </Alert>
  );
};

export default PageDeleteBlockersPanel;
